import { useState, useEffect } from 'react';
import { fetchDealerStats, DealerStats } from '@/services/dealers/dealerStatsService';
import { isSSR } from '@/lib/isSSR';

/**
 * Hook for fetching dealer statistics on the client side
 */
export const useDealerStats = () => {
  const [stats, setStats] = useState<DealerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip if we're in SSR
    if (isSSR()) {
      return;
    }

    // Check if we have server-side data
    const serverStore = (globalThis as any).__SERVER_STORE;
    if (serverStore?.dealerStats) {
      console.log('[useDealerStats] Using server-side dealer stats');
      setStats(serverStore.dealerStats);
      setLoading(false);
      return;
    }

    // Fetch dealer stats on client side
    const loadStats = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('[useDealerStats] Fetching dealer stats on client');
        
        const dealerStats = await fetchDealerStats();
        setStats(dealerStats);
      } catch (err) {
        console.error('[useDealerStats] Error fetching dealer stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch dealer stats');
        
        // Set fallback stats in case of error
        setStats({
          totalDealers: 500,
          activatedDealers: 500,
          verifiedDealers: 500
        });
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  return {
    stats,
    loading,
    error,
    // Helper to get formatted verified dealers count
    getVerifiedCount: () => {
      if (!stats) return '500+';
      const count = stats.verifiedDealers;
      return count > 0 ? `${count}+` : '500+';
    }
  };
};

/**
 * Hook for server-side dealer stats (for SSR)
 */
export const useServerDealerStats = (initialStats?: DealerStats) => {
  const [stats] = useState<DealerStats | null>(initialStats || null);

  return {
    stats,
    loading: false,
    error: null,
    // Helper to get formatted verified dealers count
    getVerifiedCount: () => {
      if (!stats) return '500+';
      const count = stats.verifiedDealers;
      return count > 0 ? `${count}+` : '500+';
    }
  };
};
